/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';
import { MenuListProps } from 'react-select/dist/declarations/src/components/Menu';
import { GroupBase } from 'react-select/dist/declarations/src/types';
import { AppLoader } from 'components/Core/AppLoader';
import { menuHeight } from './selectCustomStyles';
import { Obj } from 'types/common';
import styles from './select.module.scss';

const HEIGHT = 40;
const MAX_MENU_HEIGHT = menuHeight;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function MenuList<T extends Obj>(
  props: MenuListProps<T, boolean, GroupBase<T>>,
  optionValue?: keyof T,
  isLoading?: boolean,
  maxMenuHeight?: number
): ReactElement {
  const { options, children, getValue } = props;
  const [value] = getValue();
  const rowHeights = useRef<any>({});
  const listRef = useRef<VariableSizeList<any>>(null);
  const [rerender, setRerender] = useState(false);

  let initialOffset = 0;
  if (value) {
    const indexSelected = options.findIndex((item) => {
      return (item as T)[optionValue || 'value'] === value[optionValue || 'value'];
    });
    initialOffset = (indexSelected !== -1 ? indexSelected - 1 : 0) * HEIGHT;
  }

  const getRowHeight = useCallback((index): number => rowHeights.current[index] || HEIGHT, []);

  const setRowHeight = useCallback((index, size) => {
    if (listRef.current) listRef.current.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  }, []);

  const RenderRow = useCallback(
    ({ index, style }: { index: number; style: any }) => {
      const rowRef = useRef<HTMLDivElement | null>(null);

      useEffect(() => {
        if (rowRef.current) {
          setRowHeight(index, rowRef.current.firstElementChild?.clientHeight || HEIGHT);
        }
      }, [index, rowRef]);
      return children && Array.isArray(children) ? (
        <div ref={rowRef} style={style}>
          {children[index]}
        </div>
      ) : null;
    },
    [children, setRowHeight]
  );

  const getMenuHeight = useMemo(() => {
    if (Object.values(rowHeights.current).length) {
      const rowsHeight: number = (Object.values(rowHeights.current) as number[])
        .slice(0, 10)
        .reduce((a: number, b: number) => {
          return a + b;
        }, 0);
      return rowsHeight >= (maxMenuHeight || MAX_MENU_HEIGHT)
        ? maxMenuHeight || MAX_MENU_HEIGHT || HEIGHT
        : rowsHeight || HEIGHT;
    }
    return 120;
  }, [rowHeights, rowHeights.current, rerender, options, children]);

  if (isLoading)
    return (
      <div className={styles.menuLoader}>
        <AppLoader />
      </div>
    );

  return children && Array.isArray(children) && children.length > 0 ? (
    <VariableSizeList
      height={getMenuHeight}
      width='100%'
      itemCount={children.length}
      itemSize={getRowHeight}
      onItemsRendered={() => setRerender(!rerender)}
      initialScrollOffset={initialOffset}
      ref={listRef}>
      {RenderRow}
    </VariableSizeList>
  ) : (
    <div className={styles.menuLoader}>No items in list. Start typing to search items.</div>
  );
}
